import React, { useState, useEffect } from 'react';
import { Exam } from '../../types/exam';
import { examApi } from '../../services/exam.service';
import StudentExamCard from './StudentExamCard';
import ExamTaker from './ExamTaker';

interface StudentExamListProps {
  onClose?: () => void;
}

export const StudentExamList: React.FC<StudentExamListProps> = ({ onClose }) => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'taking'>('list');

  useEffect(() => {
    loadAvailableExams();
  }, []);

  const loadAvailableExams = async () => {
    try {
      setLoading(true);
      const data = await examApi.getAvailableExams();
      setExams(data);
      setError(null);
    } catch (err) {
      setError('Failed to load exams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = (exam: Exam) => {
    setSelectedExam(exam);
    setViewMode('taking');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedExam(null);
    loadAvailableExams();
  };

  if (viewMode === 'taking' && selectedExam) {
    return (
      <ExamTaker
        exam={selectedExam}
        onComplete={handleBackToList}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Available Exams</h1>
        {onClose && (
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!loading && exams.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No exams available at the moment</p>
        </div>
      )}

      {!loading && exams.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exams.map((exam) => (
            <StudentExamCard
              key={exam.examId}
              exam={exam}
              onStart={handleStartExam}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentExamList;
